import { API, graphqlOperation } from '@aws-amplify/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type Resource } from '../../Datasource/Resource';
import { loadMoreNotSupported, wrapPromise } from '../../Datasource/Resource';
import { type QueryFactory, type QueryFactory2, type QueryNextLoadOptions, type QueryResult } from './QueryFactory';

const noop = () => {
    console.error(
        'Refreshing data is not supported on getData. Use useAdminResource if you need refresh functionality.'
    );
    return null;
};

export const getData = <RT>(queryFactory: QueryFactory<RT>, nextToken?: string): Resource<QueryResult<RT>> => {
    const response = executeQuery<RT>(queryFactory, nextToken);
    return wrapPromise(response, noop, loadMoreNotSupported, 0, 0, 0);
};

export const executeQuery = async <RT>(
    queryFactory: QueryFactory<RT>,
    nextToken?: string
): Promise<QueryResult<RT>> => {
    const query = queryFactory(nextToken);
    return API.graphql(graphqlOperation(query)) as unknown as Promise<QueryResult<RT>>;
};

export const executeQuery2 = <T, V extends object, O = any>(
    queryFactory: QueryFactory2<T, V, O>,
    queryFactoryVariables?: V,
    options?: O
): Promise<QueryResult<T>> => {
    const { query, variables, postProcess } = queryFactory(queryFactoryVariables, options);
    if (query === null) {
        const data = {};
        return Promise.resolve({
            data: data as T,
            errors: null,
        });
    }
    const promise = API.graphql({ query, variables }) as unknown as Promise<QueryResult<T>>;
    if (postProcess) {
        return promise.then(postProcess);
    }
    return promise;
};

export const executeQuery2WithRepetitions = async <T, V extends object, O = any>(
    queryFactory: QueryFactory2<T, V, O>,
    queryFactoryVariables: V,
    options?: O,
    nextLoadOptions?: QueryNextLoadOptions<T, V>
): Promise<QueryResult<T>> => {
    let variables = queryFactoryVariables;
    let output: QueryResult<T> | undefined;
    let loops = 0;
    do {
        loops += 1;
        const response = await executeQuery2(queryFactory, variables, options);
        if (nextLoadOptions) {
            const maybeNextVariables = nextLoadOptions.getNextQueryVariables(response, variables);
            output = output ? nextLoadOptions.mergeResult(output, response) : response;
            if (!maybeNextVariables) {
                return output;
            }
            variables = maybeNextVariables;
        } else {
            return response;
        }
    } while (loops < 100);
    throw new Error('Max limit for executeQuery2WithRepetition reached');
};

export const useAdminResource = <T, V extends object, O = any>(
    query: QueryFactory2<T, V, O>,
    variablesFactory: [() => V, any[]],
    options?: O,
    nextLoadOptions?: QueryNextLoadOptions<T, V>
): Resource<QueryResult<T>> => {
    /* eslint-disable  react-hooks/exhaustive-deps */
    const variables = useMemo(variablesFactory[0], variablesFactory[1]);
    /* eslint-enable  react-hooks/exhaustive-deps */
    const [promise, setPromise] = useState<Promise<QueryResult<T>> | null>(null);
    // (#2945 bod 3) Zrcadlo AKTUÁLNÍHO promise pro anti-stale guard v onMore. Rozběhnuté dolití
    // (`await promise` klidně visí sekundy na pomalé stránce) nesmí po změně proměnných dotazu
    // — promiseFactory mezitím založil nový dotaz — pokračovat a `setPromise` přepsat čerstvý
    // dotaz výsledkem STARÉHO okna. Přesně to se dělo ve Fuel optimizeru: změna rozsahu uprostřed
    // stránkování rezervací nechala tabulku trvale na starém (užším) rozsahu, dokud uživatel
    // stránku nereloadnul. Ref se plní v setCurrentPromise (ne při renderu), takže guard vidí
    // poslední zapsaný promise i před commitem.
    const promiseRef = useRef<Promise<QueryResult<T>> | null>(null);
    const setCurrentPromise = useCallback(
        (nextPromise: Promise<QueryResult<T>>) => {
            promiseRef.current = nextPromise;
            setPromise(nextPromise);
        },
        [setPromise]
    );
    const [refreshId, setRefreshId] = useState<number>(0);
    const promiseFactory = useCallback(() => {
        setCurrentPromise(executeQuery2(query, variables, options));
    }, [query, variables, options, setCurrentPromise]);
    const onRefresh = useCallback(() => {
        promiseFactory();
        setRefreshId((rId) => rId + 1);
    }, [promiseFactory]);
    const onMore = useCallback(async (): Promise<boolean> => {
        if (!nextLoadOptions) {
            throw new Error('Query did not supply nextLoad parameters');
        }
        if (!promise) {
            throw new Error('Request not yet initialized');
        }
        const data = await promise;
        // (#2945 bod 3) Zastaralé pokračování — mezitím odstartoval nový dotaz (jiné proměnné
        // nebo refresh). Smyčka končí; dolévání převzala čerstvá instance onMore nad novým promise.
        if (promiseRef.current !== promise) {
            return false;
        }
        const nextQueryVariables = nextLoadOptions.getNextQueryVariables(data, variables);
        if (nextQueryVariables === null) {
            return false;
        }
        const newPromise = executeQuery2(query, nextQueryVariables, options).then((newData) =>
            nextLoadOptions.mergeResult(data, newData)
        );
        setRefreshId((rId) => rId + 1);
        setCurrentPromise(newPromise);
        return true;
    }, [promise, nextLoadOptions, setRefreshId, setCurrentPromise, query, options, variables]);

    useEffect(() => {
        onRefresh();
    }, [onRefresh]);

    const neverPromise = useMemo(() => new Promise<QueryResult<T>>(() => null), []);
    const [resource, setResource] = useState<Resource<QueryResult<T>>>(
        wrapPromise(neverPromise, onRefresh, onMore, -1, 0, refreshId)
    );
    useEffect(() => {
        let cancelled = false;
        if (promise !== null) {
            promise.then(
                () => {
                    if (cancelled) {
                        return;
                    }
                    if (resource.refreshId !== refreshId && resource.promise !== promise) {
                        setResource(wrapPromise(promise, onRefresh, onMore, 1, 1, refreshId));
                    }
                },
                // (#2964) Selhanou stránku řeší smyčka autoloadu (onPageError + konzole). Tenhle
                // odvozený promise ale bez druhé větve zůstal odmítnutý a bez handleru — tedy
                // unhandled rejection v konzoli prohlížeče navíc, u KAŽDÉ chyby dotazu.
                () => undefined
            );
            return () => {
                cancelled = true;
            };
        }
    }, [promise, onRefresh, onMore, refreshId, resource, setResource]);
    useEffect(() => {
        if (nextLoadOptions && nextLoadOptions.autoload && promise !== null) {
            // (#2945 bod 3) Chybná stránka nesmí skončit jako unhandled rejection — smyčka se
            // zastaví, poslední úspěšný výsledek zůstává zobrazený a příčina jde do konzole.
            // (#2964) Konzole ale nestačí tam, kde se z dat počítá: nedotečené stránky vypadají
            // jako úplná data. Kdo dodá `onPageError`, dozví se o tom a může to říct uživateli.
            onMore().catch((error: unknown) => {
                console.error('[useAdminResource] autoload page failed', error);
                nextLoadOptions.onPageError?.(error);
            });
        }
    }, [nextLoadOptions, promise, onMore]);
    return resource;
};

export const useMappedResource = <S, T>(source: Resource<S>, mapper: (source: S) => T): Resource<T> => {
    return useMemo(() => {
        let mapped: T | null = null;
        let mappedRefreshId: number | null = null;
        return {
            ...source,
            read(): T {
                if (!mapped || mappedRefreshId !== source.refreshId) {
                    const original = source.read();
                    mapped = mapper(original);
                    mappedRefreshId = source.refreshId;
                }
                return mapped;
            },
            readMaxQuality(): T {
                return mapper(source.readMaxQuality());
            },
            get promise() {
                return source.promise.then(mapper);
            },
        };
    }, [source, mapper]);
};
